import { updateAudioSettings } from "../update-audio-settings";
import { VOLUME_LABELS } from "./media-video";
import { findAncestorWithComponent, getInteractableEntity } from "../utils/scene-graph";
import {
  calcLevel,
  calcGainStepDown,
  calcGainStepUp,
  MAX_GAIN_MULTIPLIER,
  updateAvatarVolumesPref,
  getAvatarVolumePref
} from "../utils/avatar-volume-utils";
import {
  isAvatarLocallyMutedByGain,
  resolveInitialAvatarGain,
  sanitizeAvatarGain,
  toLegacyMutedFlag
} from "../utils/avatar-volume-state-utils";
import { emitAvatarVolumeChanged } from "../utils/avatar-volume-events";

AFRAME.registerComponent("avatar-volume-controls", {
  init() {
    this.volumeUp = this.volumeUp.bind(this);
    this.volumeDown = this.volumeDown.bind(this);
    this.volumeUpButton = this.el.querySelector(".avatar-volume-up-button");
    this.volumeDownButton = this.el.querySelector(".avatar-volume-down-button");
    this.muteButton = this.el.querySelector(".avatar-mute-button");
    this.volumeLabel = this.el.querySelector(".avatar-volume-label");
    this.volumeUpButton.object3D.addEventListener("interact", this.volumeUp);
    this.volumeDownButton.object3D.addEventListener("interact", this.volumeDown);
    this.update = this.update.bind(this);
    this.normalizer = null;
    const interactableEntity = getInteractableEntity(this.el);
    const networkedEntity = findAncestorWithComponent(interactableEntity, "networked-avatar");
    this.audioEl = networkedEntity.querySelector("[avatar-audio-source]");
    this.playerInfo = findAncestorWithComponent(interactableEntity, "player-info").components["player-info"];
    window.APP.store.addEventListener("statechanged", this.update);
    this.onRemoteMuteUpdated = this.onRemoteMuteUpdated.bind(this);
    this.playerInfo.el.addEventListener("remote_mute_updated", this.onRemoteMuteUpdated);
    this.muteButton.object3D.visible = this.playerInfo.data.muted;
    const volumePref = getAvatarVolumePref(this.playerInfo.displayName);
    const initialGain = resolveInitialAvatarGain({
      runtimeGain: APP.gainMultipliers.get(this.audioEl),
      prefGain: volumePref?.gainMultiplier,
      prefMutedLegacy: volumePref?.muted
    });
    this.updateGainMultiplier(initialGain, true);
  },
  remove() {
    APP.gainMultipliers.delete(this.audioEl);
    window.APP.store.removeEventListener("statechanged", this.update);
    this.playerInfo.el.removeEventListener("remote_mute_updated", this.onRemoteMuteUpdated);
  },

  updateGainMultiplier(gainMultiplier, updatePref = false) {
    const normalizedGain = sanitizeAvatarGain(gainMultiplier);
    APP.gainMultipliers.set(this.audioEl, normalizedGain);
    APP.mutedState.delete(this.audioEl);
    const audio = APP.audios.get(this.audioEl);
    if (audio) {
      updateAudioSettings(this.audioEl, audio);
    }
    this.updateVolumeLabel();
    this.el.emit("gain_multiplier_updated", { gainMultiplier: normalizedGain });
    emitAvatarVolumeChanged({
      sessionId: this.playerInfo.playerSessionId,
      gainMultiplier: normalizedGain
    });
    updatePref &&
      updateAvatarVolumesPref(this.playerInfo.displayName, normalizedGain, toLegacyMutedFlag(normalizedGain));
  },

  volumeUp() {
    let gainMultiplier = sanitizeAvatarGain(APP.gainMultipliers.get(this.audioEl));
    const step = calcGainStepUp(gainMultiplier);
    gainMultiplier = THREE.MathUtils.clamp(gainMultiplier + step, 0, MAX_GAIN_MULTIPLIER);
    this.updateGainMultiplier(gainMultiplier, true);
  },

  volumeDown() {
    let gainMultiplier = sanitizeAvatarGain(APP.gainMultipliers.get(this.audioEl));
    const step = -calcGainStepDown(gainMultiplier);
    gainMultiplier = THREE.MathUtils.clamp(gainMultiplier + step, 0, MAX_GAIN_MULTIPLIER);
    this.updateGainMultiplier(gainMultiplier, true);
  },

  updateVolumeLabel() {
    const gainMultiplier = sanitizeAvatarGain(APP.gainMultipliers.get(this.audioEl));
    const numBars = calcLevel(gainMultiplier);
    this.volumeLabel.setAttribute(
      "text",
      "value",
      isAvatarLocallyMutedByGain(gainMultiplier) ? "Muted" : VOLUME_LABELS[numBars]
    );
  },

  onRemoteMuteUpdated({ detail: { muted } }) {
    if (!this.el.sceneEl.systems.permissions.canOrWillIfCreator("mute_users")) return;
    this.muteButton.object3D.traverse(obj => {
      obj.visible = !muted;
    });
  }
});
